import { Anchor, Box, Flex, Paper, Text } from "@mantine/core"
import { CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import { IByStatus, IDashboard } from "../../../models/Dashboard"
import { currencyFormat } from "../../../utils"
import { CardHeader } from "./CardHeader"
import { STATUS_COLOR, statusTone } from "../utils"
import classes from "../real.module.css"

interface Props {
  byStatus: IDashboard["byStatus"] | undefined
  onViewAll: () => void
}

export function OrderStatusCard({ byStatus, onViewAll }: Props) {
  const { t } = useTranslation(["dashboard", "common"])
  const filtered = (byStatus ?? []).filter((s: IByStatus) => s.orderCount > 0)
  const maxCount = Math.max(...filtered.map((s) => s.orderCount), 1)

  return (
    <Paper className={classes.card}>
      <CardHeader
        title={t("dashboard:table.orderStatus")}
        helpKey="order.dashboard.section.order-status"
        subtitle={`${t("dashboard:statusSub", { defaultValue: "Төлөв бүрээр" })} · ${t("dashboard:allTime")}`}
        right={
          <Anchor onClick={onViewAll} className={classes.seeAll}>
            {t("dashboard:viewAll")}
          </Anchor>
        }
      />

      {filtered.length === 0 ? (
        <Text fz={14} c="dimmed" ta="center" py="md">
          -
        </Text>
      ) : (
        <Flex direction="column">
          {filtered.map((item, index) => (
            <StatusRow key={index} item={item} maxCount={maxCount} />
          ))}
        </Flex>
      )}
    </Paper>
  )
}

function StatusRow({ item, maxCount }: { item: IByStatus; maxCount: number }) {
  // The backend sends its own (loud) colour per status; STATUS_COLOR is only a
  // fallback. Like the status pills, it only picks one of the five tones, so a
  // row here is the same green / yellow / red / blue / gray as its pill.
  const tone = statusTone(item.name, item.color || STATUS_COLOR[item.name])
  const barWidth = Math.max((item.orderCount / maxCount) * 100, 6)

  return (
    <Box className={classes.statusItem} style={{ "--status-c": `var(--mantine-color-${tone}-filled)` } as CSSProperties}>
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" gap={6}>
          <Box className={classes.statusDot} />
          <Text className={classes.statusName}>{item.name}</Text>
        </Flex>
        <Flex align="center" gap={8}>
          <Text className={classes.statusCount}>{item.orderCount}</Text>
          <Text className={classes.statusAmount}>{currencyFormat(item.orderAmount || 0)}</Text>
        </Flex>
      </Flex>
      <Box className={classes.statusBar}>
        <Box className={classes.statusBarFill} style={{ width: `${barWidth}%` }} />
      </Box>
    </Box>
  )
}
